/**
 * @fileoverview The substitute-request criteria, edited in two places with one
 * control: Settings → Features (the organization default) and a program's
 * Settings tab (that program's own). docs/substitute-requests.md.
 *
 * The criteria only appear once requests are on — a closed door has no rules
 * worth reading. Each change is reported whole, so the caller saves the policy
 * as one value rather than stitching fields together.
 */

import { SettingsRow, Toggle } from "@/components/settings/SettingsSection";
import {
  DEFAULT_SUBSTITUTE_POLICY,
  LEAD_TIME_PRESETS,
  LIMIT_PRESETS,
  leadTimeLabel,
  limitLabel,
} from "@/lib/substitutes";

const SELECT =
  "rounded-xl border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-3 py-2 " +
  "font-sans text-[14px] text-[var(--text-primary)] outline-none focus:border-[var(--accent-yellow)] disabled:opacity-50";

/** Presets, plus the stored value when it is not one of them. */
function withCurrent(presets, current) {
  return presets.includes(current) ? presets : [...presets, current].sort((a, b) => (a ?? Infinity) - (b ?? Infinity));
}

/**
 * @param {{
 *   value: object, onChange: (next: object) => void, disabled?: boolean,
 *   testId?: string, scope?: "organization"|"program",
 * }} props
 */
export default function SubstitutePolicyFields({ value, onChange, disabled = false, testId = "sub-policy", scope = "organization" }) {
  const policy = { ...DEFAULT_SUBSTITUTE_POLICY, ...(value || {}) };
  const set = (patch) => onChange({ ...policy, ...patch });
  const leadOptions = withCurrent(LEAD_TIME_PRESETS, policy.min_lead_hours);
  const limitOptions = withCurrent(LIMIT_PRESETS, policy.max_requests);

  return (
    <div data-testid={testId}>
      <SettingsRow
        title="Instructors can request a substitute"
        desc={
          scope === "program"
            ? "A Request sub button on this program's upcoming class sessions. You approve each request and choose who covers."
            : "A Request sub button on upcoming class sessions in every program that uses this default. You approve each request and choose who covers."
        }
        trailing={
          <Toggle
            on={policy.enabled}
            onChange={(on) => set({ enabled: on })}
            disabled={disabled}
            label="Instructors can request a substitute"
            testId={`${testId}-enabled`}
          />
        }
        last={!policy.enabled}
      />
      {policy.enabled && (
        <div className="rounded-b-[20px] bg-[var(--studio-overlay)]" data-testid={`${testId}-criteria`}>
          <SettingsRow
            title="Minimum notice"
            desc="How long before a session starts a request must arrive. Closer than this, the instructor is asked to contact you directly."
            trailing={
              <select
                value={policy.min_lead_hours}
                onChange={(e) => set({ min_lead_hours: Number(e.target.value) })}
                disabled={disabled}
                aria-label="Minimum notice"
                data-testid={`${testId}-lead`}
                className={SELECT}
              >
                {leadOptions.map((h) => (
                  <option key={h} value={h}>
                    {leadTimeLabel(h)}
                  </option>
                ))}
              </select>
            }
          />
          <SettingsRow
            title="Requests before contacting an admin"
            desc="Per instructor, per program, for the program's whole run. Declined and withdrawn requests don't count."
            trailing={
              <select
                value={policy.max_requests === null ? "none" : policy.max_requests}
                onChange={(e) => set({ max_requests: e.target.value === "none" ? null : Number(e.target.value) })}
                disabled={disabled}
                aria-label="Requests before contacting an admin"
                data-testid={`${testId}-limit`}
                className={SELECT}
              >
                {limitOptions.map((n) => (
                  <option key={n ?? "none"} value={n ?? "none"}>
                    {limitLabel(n)}
                  </option>
                ))}
              </select>
            }
          />
          <SettingsRow
            title="Lesson plan required"
            desc="The instructor leaves a plan with the request. The substitute reads it before the session."
            trailing={
              <Toggle
                on={policy.lesson_plan_required}
                onChange={(on) => set({ lesson_plan_required: on })}
                disabled={disabled}
                label="Lesson plan required"
                testId={`${testId}-plan`}
              />
            }
            last
          />
        </div>
      )}
    </div>
  );
}
